import type { DTOBlock } from '@/api/generated-catch-backend/model/dTOBlock';
import type { Block } from '@/api/generated-catch-backend/model/block';
import { PortableText as PortableTextRenderer } from '@portabletext/react';

export type DTOBlockContent = DTOBlock[];
export type BlockContent = (DTOBlock | Block)[];

// Define the PortableText format expected by @portabletext/react
interface PortableTextBlock {
  _type: 'block';
  _key: string;
  style?: string;
  listItem?: string;
  level?: number;
  children: PortableTextSpan[];
  markDefs?: PortableTextMarkDef[];
}

interface PortableTextSpan {
  _type: 'span';
  _key: string;
  text: string;
  marks?: string[];
}

interface PortableTextMarkDef {
  _type: string;
  _key: string;
  href?: string;
}

interface PortableTextProps {
  /**
   * The block content to render
   */
  content: BlockContent | null | undefined;
  /**
   * Custom className for the container
   */
  className?: string;
  /**
   * Whether to render as inline content (no wrapper div)
   */
  inline?: boolean;
  /**
   * Content to render inline at the end of the text
   */
  trailingContent?: React.ReactNode;
}

/**
 * Convert DTOBlock or Block format to PortableText format
 */
function convertDTOBlocksToPortableText(blocks: BlockContent): PortableTextBlock[] {
  if (!blocks || !Array.isArray(blocks) || blocks.length === 0) {
    return [];
  }

  return blocks.map((block, index) => {
    // Ensure block is an object
    if (!block || typeof block !== 'object') {
      return {
        _type: 'block' as const,
        _key: `block-${index}`,
        style: 'normal',
        children: [],
        markDefs: undefined,
      };
    }

    // Convert spans with defensive checks
    const children: PortableTextSpan[] = Array.isArray(block.children)
      ? block.children.map((span, spanIndex) => {
          // Ensure span is an object with text property
          if (!span || typeof span !== 'object') {
            return {
              _type: 'span' as const,
              _key: `span-${index}-${spanIndex}`,
              text: '',
              marks: [],
            };
          }

          return {
            _type: 'span' as const,
            _key: `span-${index}-${spanIndex}`,
            text: span.text || '',
            marks: Array.isArray(span.marks) ? span.marks : [],
          };
        })
      : [];

    // Convert mark definitions with defensive checks
    const markDefs: PortableTextMarkDef[] = Array.isArray(block.markDefs)
      ? block.markDefs.map((markDef, markIndex) => {
          if (!markDef || typeof markDef !== 'object') {
            return {
              _type: 'link',
              _key: `mark-${index}-${markIndex}`,
              href: '',
            };
          }

          return {
            _type: 'link', // Default to 'link' since DTOMarkDef doesn't have _type
            _key: `mark-${index}-${markIndex}`,
            href: markDef.href || '',
          };
        })
      : [];

    return {
      _type: 'block' as const,
      _key: `block-${index}`,
      style: block.style || 'normal',
      listItem: block.listItem || undefined,
      level: block.level || undefined,
      children,
      markDefs: markDefs.length > 0 ? markDefs : undefined,
    };
  });
}

/**
 * Create custom components for rendering different block types
 * Using 'unknown' type for component props to maintain type safety
 */
function createComponents(inline = false) {
  return {
    marks: {
      link: (props: unknown) => {
        const { value, children } = props as { value?: { href?: string }; children: React.ReactNode };
        return (
          <a
            href={value?.href || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            {children}
          </a>
        );
      },
      strong: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return <strong className="font-semibold">{children}</strong>;
      },
      em: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return <em className="italic">{children}</em>;
      },
      sup: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return <sup className="align-super text-xs">{children}</sup>;
      },
      small: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        // we need block so the span creates it's own bounding box and we can set the line-height
        return <span className="block text-xs leading-tight">{children}</span>;
      },
    },
    block: {
      // Use span for inline, block elements for normal to maintain proper HTML structure
      // whitespace-pre-line preserves newlines from the content while still wrapping text normally
      // min-h-[1.5em] ensures consistent spacing even when a block is empty or contains only newlines
      normal: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return inline ? (
          <span className="whitespace-pre-line">{children}</span>
        ) : (
          <p className="mb-4 min-h-[1.5em] whitespace-pre-line last:mb-0">{children}</p>
        );
      },
      h1: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return inline ? <span className="text-3xl">{children}</span> : <h1 className="mb-4 text-3xl">{children}</h1>;
      },
      h2: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return inline ? <span className="text-2xl">{children}</span> : <h2 className="mb-3 text-2xl">{children}</h2>;
      },
      h3: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return inline ? <span className="text-xl">{children}</span> : <h3 className="mb-2 text-xl">{children}</h3>;
      },
      h4: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return inline ? <span className="text-lg">{children}</span> : <h4 className="mb-2 text-lg">{children}</h4>;
      },
      h5: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return inline ? <span className="text-base">{children}</span> : <h5 className="mb-2 text-base">{children}</h5>;
      },
      h6: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return inline ? <span className="text-sm">{children}</span> : <h6 className="mb-2 text-sm">{children}</h6>;
      },
      blockquote: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return inline ? (
          <span className="italic">{children}</span>
        ) : (
          <blockquote className="my-4 border-l-4 border-gray-300 pl-4 italic">{children}</blockquote>
        );
      },
    },
    list: {
      bullet: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return <ul className="mb-4 list-inside list-disc">{children}</ul>;
      },
      number: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return <ol className="mb-4 list-inside list-decimal">{children}</ol>;
      },
    },
    listItem: {
      bullet: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return <li className="mb-1">{children}</li>;
      },
      number: (props: unknown) => {
        const { children } = props as { children: React.ReactNode };
        return <li className="mb-1">{children}</li>;
      },
    },
  };
}

/**
 * PortableText wrapper component that handles DTOBlock content
 */
export function PortableText({ content, className, inline = false, trailingContent }: PortableTextProps) {
  // Handle empty content
  if (!content || content.length === 0) {
    return null;
  }

  try {
    // Convert DTOBlocks to PortableText format
    const portableTextContent = convertDTOBlocksToPortableText(content);

    // Handle empty converted content
    if (!portableTextContent || portableTextContent.length === 0) {
      return null;
    }

    // Render the content
    const renderedContent = <PortableTextRenderer value={portableTextContent} components={createComponents(inline)} />;

    // Return inline or wrapped content
    if (inline) {
      return (
        <>
          {renderedContent}
          {trailingContent}
        </>
      );
    }

    return (
      <div className={className}>
        {renderedContent}
        {trailingContent}
      </div>
    );
  } catch (error) {
    console.error('Error in PortableText component:', error);
    console.error('Content that caused error:', content);

    // Fallback to simple text rendering
    if (typeof content === 'string') {
      return inline ? <>{content}</> : <div className={className}>{content}</div>;
    }

    // For non-string content, try to extract text
    const fallbackText = Array.isArray(content)
      ? content
          .map((block) =>
            Array.isArray(block?.children) ? block.children.map((span) => span?.text || '').join('') : '',
          )
          .join(' ')
      : '';

    return fallbackText ? inline ? <>{fallbackText}</> : <div className={className}>{fallbackText}</div> : null;
  }
}

/**
 * Helper function to check if content is empty
 */
export function isPortableTextEmpty(content: BlockContent | null | undefined | string | unknown): boolean {
  try {
    // Handle null, undefined, or empty string
    if (!content) {
      return true;
    }

    // Handle string input
    if (typeof content === 'string') {
      return content.trim() === '';
    }

    // Handle non-array input
    if (!Array.isArray(content)) {
      return true;
    }

    // Handle empty array
    if (content.length === 0) {
      return true;
    }

    // Check if all blocks are empty (no text content)
    return content.every((block: unknown) => {
      try {
        // Check if block exists and is an object
        if (!block || typeof block !== 'object') {
          return true; // Empty block
        }

        const blockObj = block as { children?: unknown[] };

        // Check if children exists and is an array
        if (!blockObj.children || !Array.isArray(blockObj.children)) {
          return true; // No children means empty
        }

        // Check if children array is empty
        if (blockObj.children.length === 0) {
          return true; // Empty children array
        }

        // Check if all spans in children are empty
        return blockObj.children.every((span: unknown) => {
          try {
            // Check if span exists and is an object
            if (!span || typeof span !== 'object') {
              return true; // Empty span
            }

            const spanObj = span as { text?: string };

            // Check if text exists and has content
            if (!spanObj.text || typeof spanObj.text !== 'string') {
              return true; // No text or text is not a string
            }

            // Check if text content is empty when trimmed
            return spanObj.text.trim() === '';
          } catch (spanError) {
            console.warn('Error checking span:', spanError, span);
            return true; // Treat error as empty span
          }
        });
      } catch (blockError) {
        console.warn('Error checking block:', blockError, block);
        return true; // Treat error as empty block
      }
    });
  } catch (error) {
    console.error('Error in isPortableTextEmpty:', error, content);
    // If we can't determine if it's empty, assume it has content to be safe
    return false;
  }
}

export default PortableText;
